import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { usePortal } from '@/contexts/PortalContext'
import {
  ShoppingBag, Phone, Mail, MapPin, Smartphone, Wifi, Signal,
  RefreshCw, ShieldCheck, Headphones, Router, Tv,
} from 'lucide-react'

export default function PortalHome() {
  const { portal } = usePortal()
  // Coverage checker, mirrored from the main site's home page: the postcode box lives here
  // and hands off to the full /network-coverage page, which is the same checker the main
  // site uses. Resellers were missing it entirely.
  const navigate = useNavigate()
  const [coveragePostcode, setCoveragePostcode] = useState('')
  const handleCoverageCheck = (e: React.FormEvent) => {
    e.preventDefault()
    const pc = coveragePostcode.trim().toUpperCase()
    navigate(pc ? `/network-coverage?postcode=${encodeURIComponent(pc)}` : '/network-coverage')
  }
  const primaryColor = portal?.primary_color || '#0ea5e9'
  const brandName = portal?.trading_name || portal?.company_name || 'Welcome'
  const shopEnabled = (portal as any)?.shop_enabled ?? true

  // Neutral, reseller-agnostic advertising — works for any white-label portal.
  const features = [
    { icon: Signal, title: 'UK-wide coverage', text: 'Reliable 4G & 5G connectivity reaching 99% of the UK population.' },
    { icon: Smartphone, title: 'Mobile made simple', text: 'Flexible SIM plans with generous data — perfect for life on the go.' },
    { icon: Wifi, title: 'Broadband for home', text: 'Fast, steady home broadband for streaming, gaming and working from home.' },
    { icon: RefreshCw, title: 'Switch with ease', text: 'Keep your number, no long lock-ins and no hidden surprises.' },
    { icon: ShieldCheck, title: 'Safe & secure', text: 'Your details are protected with bank-level security at every step.' },
    { icon: Headphones, title: 'Friendly support', text: 'A real, helpful team on hand whenever you need a hand.' },
  ]

  return (
    <div className="space-y-0">
      {/* Hero — neutral designed banner (no logo; the logo lives in the header) */}
      <section className="relative overflow-hidden py-24 px-4 text-white text-center" style={{ backgroundColor: primaryColor }}>
        {/* soft depth without an image — works on any brand colour */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/30 pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-black/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto">
          {/* Hero copy is the reseller's to set. hero_title and hero_subtitle were editable
              in admin, saved, and served by /portal/config — but this heading was hardcoded,
              so anything typed into them vanished. They now drive the banner, with the
              previous wording as the fallback so a portal that sets neither looks unchanged.
              NO brand name/logo here — brand = header logo + colour. */}
          <h1 className="text-4xl sm:text-6xl font-extrabold mb-4 tracking-tight">
            {portal?.hero_title || <>Mobile &amp; broadband,<br className="hidden sm:block" /> made simple</>}
          </h1>
          <p className="text-xl sm:text-2xl opacity-95 mb-4 max-w-2xl mx-auto">
            {portal?.hero_subtitle || portal?.tagline || "Great-value plans on the UK's leading networks."}
          </p>
          {portal?.welcome_message && <p className="text-base opacity-80 mb-2 max-w-xl mx-auto">{portal.welcome_message}</p>}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            {shopEnabled && (
              <Link
                to="/shop"
                className="px-8 py-3 bg-white rounded-xl font-semibold hover:bg-gray-50 transition-colors shadow-sm"
                style={{ color: primaryColor }}
              >
                <span className="flex items-center justify-center gap-2"><ShoppingBag className="w-5 h-5" /> Browse Plans</span>
              </Link>
            )}
            <Link
              to="/register"
              className={shopEnabled
                ? 'px-8 py-3 bg-white/15 border border-white/40 rounded-xl font-semibold text-white hover:bg-white/25 transition-colors'
                : 'px-8 py-3 bg-white rounded-xl font-semibold hover:bg-gray-50 transition-colors shadow-sm'}
              style={shopEnabled ? undefined : { color: primaryColor }}
            >
              Create Account
            </Link>
          </div>
        </div>
      </section>

      {/* Two audiences — mobile + broadband, kept generic for any reseller */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl p-8 border border-gray-100 shadow-sm" style={{ backgroundColor: `${primaryColor}0d` }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${primaryColor}1f` }}>
              <Smartphone className="w-7 h-7" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">For your mobile</h3>
            <p className="text-gray-600 leading-relaxed mb-5">
              SIM-only and pay-as-you-go plans with the data you need and the freedom you want — keep your number and stay
              connected wherever you are.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2"><Signal className="w-4 h-4" style={{ color: primaryColor }} /> 4G &amp; 5G on leading UK networks</li>
              <li className="flex items-center gap-2"><RefreshCw className="w-4 h-4" style={{ color: primaryColor }} /> No long-term lock-ins</li>
            </ul>
          </div>
          <div className="rounded-3xl p-8 border border-gray-100 shadow-sm" style={{ backgroundColor: `${primaryColor}0d` }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${primaryColor}1f` }}>
              <Router className="w-7 h-7" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">For your home</h3>
            <p className="text-gray-600 leading-relaxed mb-5">
              Dependable home broadband built for the way you live — stream, game, video-call and work from home without
              the buffering.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2"><Tv className="w-4 h-4" style={{ color: primaryColor }} /> Smooth streaming on every screen</li>
              <li className="flex items-center gap-2"><Wifi className="w-4 h-4" style={{ color: primaryColor }} /> Whole-home wireless coverage</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Coverage checker — same tool as the main site, painted in the reseller's colour */}
      <section className="py-16 sm:py-20 px-4 text-white" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/25 mb-5">
            <Signal className="w-5 h-5" />
            <span className="text-sm font-medium">Network Coverage Checker</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Check the coverage in your area</h2>
          <p className="opacity-90 mb-8">
            Enter your postcode to see 4G and 5G coverage from all UK networks — Three, Vodafone, O2 and EE.
          </p>
          <form onSubmit={handleCoverageCheck} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <div className="relative flex-1">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
              <input
                type="text"
                value={coveragePostcode}
                onChange={(e) => setCoveragePostcode(e.target.value.toUpperCase())}
                placeholder="Enter your postcode (e.g. SW1A 1AA)"
                maxLength={10}
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/15 border-2 border-white/25 text-white placeholder-white/60 focus:border-white focus:outline-none text-lg font-mono"
              />
            </div>
            <button type="submit"
                    className="px-8 py-4 bg-white rounded-xl font-bold shadow-sm hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    style={{ color: primaryColor }}>
              <Signal className="w-5 h-5" /> Check Coverage
            </button>
          </form>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm opacity-80">
            {['Three', 'Vodafone', 'O2', 'EE'].map(n => (
              <span key={n} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-white/70" />{n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid — neutral advertising */}
      <section className="py-16 sm:py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Why choose {brandName}?</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Everything you'd expect from a modern provider — and nothing you wouldn't.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 bg-white p-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                  <f.icon className="w-6 h-6" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1.5">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          {[
            { stat: '99%', label: 'UK population covered' },
            { stat: '5G', label: 'Ready where available' },
            { stat: '£0', label: 'Hidden fees' },
            { stat: '7 days', label: 'Friendly support' },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-3xl sm:text-4xl font-extrabold" style={{ color: primaryColor }}>{s.stat}</p>
              <p className="text-sm text-gray-600 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About (only when the reseller has written one) */}
      {portal?.about_blurb && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About Us</h2>
            <p className="text-gray-600 leading-relaxed">{portal.about_blurb}</p>
          </div>
        </section>
      )}

      {/* Closing call to action */}
      <section className="py-16 px-4 text-white text-center" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-3">Ready to get started?</h2>
          <p className="opacity-90 mb-8">Join {brandName} today — it only takes a couple of minutes.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="px-8 py-3 bg-white rounded-xl font-semibold hover:bg-gray-50 transition-colors" style={{ color: primaryColor }}>
              Create Account
            </Link>
            {shopEnabled && (
              <Link to="/shop" className="px-8 py-3 bg-white/15 border border-white/40 rounded-xl font-semibold text-white hover:bg-white/25 transition-colors">
                Browse Plans
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Contact (only when the reseller has filled it in) */}
      {(portal?.contact_phone || portal?.contact_email || portal?.contact_address) && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Contact Us</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {portal.contact_phone && (
                <div className="text-center bg-white rounded-2xl p-6 shadow-sm">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
                    <Phone className="w-6 h-6" style={{ color: primaryColor }} />
                  </div>
                  <p className="font-semibold text-gray-900 mb-1">Phone</p>
                  <a href={`tel:${portal.contact_phone}`} className="text-sm hover:underline" style={{ color: primaryColor }}>{portal.contact_phone}</a>
                </div>
              )}
              {portal.contact_email && (
                <div className="text-center bg-white rounded-2xl p-6 shadow-sm">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
                    <Mail className="w-6 h-6" style={{ color: primaryColor }} />
                  </div>
                  <p className="font-semibold text-gray-900 mb-1">Email</p>
                  <a href={`mailto:${portal.contact_email}`} className="text-sm hover:underline" style={{ color: primaryColor }}>{portal.contact_email}</a>
                </div>
              )}
              {portal.contact_address && (
                <div className="text-center bg-white rounded-2xl p-6 shadow-sm">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
                    <MapPin className="w-6 h-6" style={{ color: primaryColor }} />
                  </div>
                  <p className="font-semibold text-gray-900 mb-1">Address</p>
                  <p className="text-sm text-gray-600">{portal.contact_address}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
